'use client'

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { debounce } from 'lodash'

import { User } from '@/lib/types/UserT'
import useClickOutside from '@/lib/hooks/useClickOutside'
import { cn } from '@/lib/utils'
import UserSelectInput from '@/components/UserSelectField/SelectInput'
import UserDropdownItem from '@/components/UserSelectField/DropdownItem'
import { POP_CLASS } from '@/components/livecontact/fieldStyles'
import Spinner from '@/components/livecontact/Spinner'
import LcIcon from '@/components/livecontact/icons'

type UserSelectFieldProps = {
  placeholder?: string
  onSearch: (search: string) => void
  isFetching: boolean
  emptyText: string
  userList: User[]
  isMultiple?: boolean
  value: Pick<User, 'id' | 'companyRole' | 'firstName' | 'lastName' | 'email'>[]
  onChange: (user: Pick<User, 'id' | 'companyRole' | 'firstName' | 'lastName' | 'email'>[]) => void
}

/** Async user picker: chips in the field, POP menu with avatar rows, Escape closes, Backspace pops the last chip. */
const UserSelectField = ({
  placeholder = 'Search users…',
  onChange,
  onSearch,
  value,
  userList,
  isFetching,
  isMultiple = false,
  emptyText,
}: UserSelectFieldProps) => {
  const [inputValue, setInputValue] = useState('')
  const [isFocused, setIsFocused] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const debouncedSearch = useMemo(() => debounce(onSearch, 500), [onSearch])

  const selectedUsers = useMemo<User[]>(() => (value as User[]) ?? [], [value])

  const handleInputChange = (val: string) => {
    setInputValue(val)
    debouncedSearch(val)
  }

  const handleMemberSelect = (member: User) => {
    if (isMultiple) {
      const exists = selectedUsers.some((u) => u.id === member.id)
      const newValue = exists
        ? selectedUsers.filter((u) => u.id !== member.id)
        : [...selectedUsers, member]
      onChange(newValue)
    } else {
      onChange([member])
      setIsFocused(false)
    }
    handleInputChange('')
    inputRef.current?.focus()
  }

  const handleRemoveUser = (id: number) => {
    if (isMultiple) {
      onChange(selectedUsers.filter((u) => u.id !== id) as any)
    } else {
      onChange(null as any)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && isMultiple && inputValue === '' && selectedUsers.length > 0) {
      const newValue = selectedUsers.slice(0, -1)
      onChange(newValue as any)
    }
    if (e.key === 'Escape') setIsFocused(false)
  }

  // Close dropdown when clicking outside
  const handleClickOutside = useCallback(() => {
    setIsFocused(false)
  }, [])

  useClickOutside(wrapperRef, handleClickOutside, isFocused)

  useEffect(() => {
    return () => debouncedSearch.cancel()
  }, [debouncedSearch])

  const isSearching = inputValue.trim().length > 0

  return (
    <div ref={wrapperRef} className="relative">
      <UserSelectInput
        selectedUsers={selectedUsers}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        onRemoveUser={handleRemoveUser}
        onKeyDown={handleKeyDown}
        inputRef={inputRef}
        placeholder={placeholder}
        isMultiple={isMultiple}
        onFocus={() => setIsFocused(true)}
      />

      {isFocused && (
        <div
          role="listbox"
          aria-multiselectable={isMultiple}
          className={cn(
            'absolute left-0 right-0 top-full z-50 mt-1.5 max-h-64 origin-top overflow-y-auto animate-lc-pop lc-scrollbar-thin',
            POP_CLASS
          )}
        >
          {isFetching ? (
            <div
              className="flex items-center justify-center gap-2 py-4 text-[13px] text-ink-4"
              role="status"
            >
              <Spinner className="size-3.5" />
              {isSearching ? 'Searching…' : 'Loading…'}
            </div>
          ) : userList.length > 0 ? (
            <div className="flex flex-col gap-0.5">
              {userList.map((person) => {
                const isSelected = selectedUsers.some((u) => u.id === person.id)
                return (
                  <UserDropdownItem
                    key={person.id}
                    user={person}
                    isSelected={isSelected}
                    onSelect={handleMemberSelect}
                  />
                )
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center gap-1.5 px-3 py-5 text-center">
              <span className="inline-flex size-8 items-center justify-center rounded-[8px] bg-surface-2 text-ink-4 dark:bg-surface-3">
                <LcIcon name={isSearching ? 'search' : 'users'} />
              </span>
              <span className="text-[13px] leading-[1.4] tracking-[-0.2px] text-ink-4">
                {isSearching ? `No one matches “${inputValue.trim()}”.` : emptyText}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default UserSelectField
